import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Table,
  Breadcrumb,
  Result,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import get from "lodash/get";
import dayjs from "dayjs";
import { getOperatorDetailColumns } from "../../constants/tableColumns/summaryReportsColumns/getOperatorDetailColumns";
import { fetchCompanies } from "../../redux/actions/companyActions";
import {
  UsergroupAddOutlined,
  BorderlessTableOutlined,
} from "@ant-design/icons";
import { fetchOperatorDetailReport } from "../../redux/actions/reportActions";
import { fetchOperatorDetailReportPdf } from "../../redux/actions/downloadPdfAction";
import { fetchCompanyStaffs } from "../../redux/actions/companyActions";
import HeaderSelector from "../../components/reports/operatordetailReport/headerSelector";
import requireAuth from "../../hocs/requireAuth";
import { useExcelExport } from "../../hooks/useExcelExport";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { COMPANY_ADMIN_ROLE_ACCESS } from "../../constants";

const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <UsergroupAddOutlined style={{ fontSize: "16px" }} />
              <span>Operator</span>
            </>
          ),
        },
        {
          title: "Detail Report",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
  templateType: undefined,
  operator: undefined,
  date: dayjs(),
};

const OperatorDetailReport = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [operatorDetails, setOperatorDetails] = useState(undefined);
  const [showEmpty, setShowEmpty] = useState(false);
  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { wrapperRef, onDownload, isReady } = useExcelExport(
    [operatorDetails,getOperatorDetailColumns],
    `Operator-detail-report-${get(filter, "date").format("MM-YYYY")}`,
    `Operator-detail`
  );

  const setinitialCompany = (company) => {
    setConfig({
      ...config,
      companies: [company],
    });
    form.setFieldsValue({
      template: undefined,
      operator: undefined,
      Company: get(company, "id"),
    });
    getCompanyOperators(company);
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getCompanyOperators = (company) => {
    setLoading(true);
    dispatch(fetchCompanyStaffs(get(company, "id"),{ role_id: 3 }))
      .then((operators) => {
        setConfig({
          ...config,
          templates: get(company, "template_access_list"),
          operators,
        });
        setFilters({
          ...filter,
          company,
          templateType: undefined,
          operator: undefined,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getDetails = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(fetchOperatorDetailReport(get(filter, "operator.id"), query))
      .then((result) => {
        if (get(result, "reports", []).length > 0) {
          setShowEmpty(false);
          setOperatorDetails(result);
        } else {
          setOperatorDetails(undefined);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      date: get(filter, "date").format("MM-YYYY"),
    };
    dispatch(
      fetchOperatorDetailReportPdf(get(filter, "operator.id"), {
        ...query,
        operator_name: get(filter, "operator.name"),
      })
    )
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Form form={form}>
          <Spin spinning={loading}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setConfig={setConfig}
              setFilters={setFilters}
              getCompanyOperators={getCompanyOperators}
              getDetails={getDetails}
              setOperatorDetails={setOperatorDetails}
            />
            {get(operatorDetails, "reports", []).length > 0 && (
              <div className="w-full px-8 overflow-y-auto">
                <div className="float-right mb-5 space-x-2">
                  <Tooltip className="mb-5" title="Download Report">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadReport()}
                    />
                  </Tooltip>
                  <Tooltip title="Excel Summary Report">
                    <ExcelExportButton
                      onDownload={onDownload}
                      disabled={!isReady}
                    />
                  </Tooltip>
                </div>
                <div className="w-full table-container" ref={wrapperRef}>
                  <Title className="mt-10" level={5}>
                    Operator Details : {get(filter, "operator.name")}
                  </Title>
                  <Table
                    className="mb-20 mt-10 custom-horizontal-scrollbar"
                    dataSource={get(operatorDetails, "reports", [])}
                    columns={getOperatorDetailColumns(
                      get(operatorDetails, "shifts", []),
                      get(filter,"templateType")
                    )}
                    bordered
                    pagination={false}
                   rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                  />
                </div>
              </div>
            )}

            {showEmpty && (
              <div className="px-8 py-8">
                <Result
                  title={`No operator exists...for this month!`}
                  subTitle="Please try again with another company, template type or month"
                  status="404"
                />
              </div>
            )}
          </Spin>
        </Form>
      </div>
    </div>
  );
};

export default requireAuth(OperatorDetailReport,COMPANY_ADMIN_ROLE_ACCESS);
